import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Truck, Ship, CheckCircle, ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import SectionHeading from "@/components/shared/SectionHeading";
import CTASection from "@/components/home/CTASection";

const SLIDESHOW_INTERVAL_MS = 3500;
const SCROLL_OFFSET_PX = 160;

// Animation variants (module-level pour éviter re-renders inutiles)
const fadeUp = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7 } };
const fadeUpInView = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

const SLIDESHOW_IMGS = [
  "/images/camion-plateau.jpeg",
  "/images/port-rotterdam.png",
  "/images/camion-citerne.jpeg",
  "/images/camion-fond-blanc.png",
  "/images/tracteur-tricolore.jpg",
  "/images/photo-equipe.jpeg",
  "/images/whatsapp-camion-1.jpg",
  "/images/whatsapp-camion-2.jpg",
  "/images/whatsapp-camion-3.jpeg",
];

function TruckSlideshow() {
  const [current, setCurrent] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrent((c) => (c + 1) % SLIDESHOW_IMGS.length);
        setFade(true);
      }, 400);
    }, SLIDESHOW_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [setFade, setCurrent, SLIDESHOW_IMGS, SLIDESHOW_INTERVAL_MS]);

  return (
    <div className="relative rounded-2xl overflow-hidden w-full" style={{ height: "clamp(260px, 50vw, 480px)" }}>
      <img
        src={SLIDESHOW_IMGS[current]}
        alt="Transport Routier"
        className="w-full h-full object-contain"
        style={{ opacity: fade ? 1 : 0, transition: "opacity 0.4s ease" }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#152540]/40 to-transparent" />
      {/* Points indicateurs */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
        {SLIDESHOW_IMGS.map((src, i) => (
          <button
            key={src}
            onClick={() => { setFade(false); setTimeout(() => { setCurrent(i); setFade(true); }, 400); }}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i === current ? "bg-[#E8902A] w-4" : "bg-white/40"}`}
          />
        ))}
      </div>
    </div>
  );
}

const services = [
  {
    icon: <Truck size={32} />,
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    title: "Transport Routier",
    subtitle: "National & Européen",
    slideshow: true,
    desc: "Nous organisons vos expéditions nationales et européennes pour tous types de marchandises. Que ce soit en lot complet, groupage ou express, nous trouvons la solution adaptée à votre besoin et à votre budget.",
    features: [
      "Lots complets (FTL) et groupage (LTL)",
      "Transport Express",
      "Transport exceptionnel",
      "Marchandises générales",
      "Marchandises spécifiques",
      "Vrac",
    ],
  },
  {
    icon: <Ship size={32} />,
    color: "text-[#E8902A]",
    bg: "bg-[#E8902A]/10",
    border: "border-[#E8902A]/20",
    title: "Fret Maritime",
    subtitle: "Overseas — Maritime",
    img: "/images/services-hero-bg.png",
    imgClass: "w-full h-auto object-contain",
    desc: "Du conteneur de groupage au conteneur complet, nous élaborons pour vous les meilleures solutions de transport maritime à travers le monde.",
    features: [
      "Conteneurs complets (FCL)",
      "Conteneurs de groupage (LCL)",
      "Dry – High Cube – Reefer – Open top – ADR...",
      "Prestations Door to Door",
      "Tarification simple et compétitive",
      "Suivi de votre expédition",
    ],
  },
];

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        setTimeout(() => {
          const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET_PX;
          window.scrollTo({ top, behavior: "smooth" });
        }, 100);
      }
    }
  }, [hash, SCROLL_OFFSET_PX]);

  return (
    <div data-testid="services-page">
      <Helmet>
        <title>Nos Services — Transport Routier & Fret Maritime | Pierre Affrètement</title>
        <link rel="canonical" href="https://pierreaffretement.fr/services" />
        <meta name="description" content="Transport routier national et européen, fret maritime international. Pierre Affrètement organise vos expéditions depuis Vichy (03)." />
      </Helmet>
      {/* Hero */}
      <section className="relative py-32 pt-44 bg-[#1a2e48] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/services-bg-alt.jpeg"
            alt="Services logistiques"
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2e48]/20 to-[#1a2e48]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#E8902A]" />
              <span className="font-heading font-bold text-xs tracking-widest uppercase text-[#E8902A]">
                Nos Services
              </span>
            </div>
            <h1 className="font-heading font-black text-5xl md:text-6xl text-white leading-[0.95] uppercase mb-4">
              Route & Mer,
              <br />
              <span className="gradient-text">au service</span>
              <br />
              <span className="text-4xl md:text-5xl font-heading font-bold text-slate-200">de vos marchandises.</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-xl leading-relaxed">
              Transport routier national & européen, fret maritime intercontinental — Découvrez le détail de nos prestations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services détaillés */}
      <section className="py-16 bg-[#152540]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-24">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              id={service.title === "Fret Maritime" ? "fret-maritime" : "transport-routier"}
              {...fadeUpInView}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
              data-testid={`service-detail-${i}`}
            >
              <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-px w-8 bg-[#E8902A]" />
                  <span className="font-heading font-bold text-xs tracking-widest uppercase text-[#E8902A]">
                    {service.subtitle}
                  </span>
                </div>

                <div className={`w-14 h-14 rounded-2xl ${service.bg} ${service.color} flex items-center justify-center mb-5`}>
                  {service.icon}
                </div>

                <h2 className="font-heading font-black text-4xl text-white mb-4 uppercase">{service.title}</h2>
                <p className="text-slate-400 text-base leading-relaxed mb-8">{service.desc}</p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 sm:grid-flow-col sm:grid-rows-3 gap-2 mb-8">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-300 text-sm">
                      <CheckCircle size={16} className={`${service.color} mt-0.5 flex-shrink-0`} />
                      {f}
                    </li>
                  ))}
                </ul>

                {service.title === "Fret Maritime" ? (
                  <Link to="/contact" data-testid={`service-contact-btn-${i}`}>
                    <button className="flex items-center gap-2 border border-[#E8902A]/60 hover:border-[#E8902A] text-[#E8902A] hover:bg-[#E8902A]/10 font-heading font-bold text-sm tracking-widest uppercase px-6 py-3 transition-all duration-300">
                      Nous contacter
                      <ArrowRight size={16} />
                    </button>
                  </Link>
                ) : (
                  <Link to="/devis" data-testid={`service-devis-btn-${i}`}>
                    <button className="flex items-center gap-2 bg-[#E8902A] hover:bg-[#C26218] text-white font-heading font-bold text-sm tracking-widest uppercase px-6 py-3 transition-all duration-300 hover:-translate-y-0.5 shadow-[0_4px_14px_0_rgba(232,144,42,0.35)]">
                      Demander un devis
                      <ArrowRight size={16} />
                    </button>
                  </Link>
                )}
              </div>

              <div className={`relative ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                {service.slideshow ? (
                  <TruckSlideshow />
                ) : (
                  <div className="relative rounded-2xl overflow-hidden">
                    <img
                      src={service.img}
                      alt={service.title}
                      className={service.imgClass}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#152540]/40 to-transparent" />
                    <div className={`absolute bottom-5 left-5 ${service.bg} border ${service.border} rounded-xl px-5 py-3 backdrop-blur-sm`}>
                      <div className={`font-heading font-bold ${service.color} text-sm uppercase tracking-widest`}>{service.subtitle}</div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
